import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	InputSignal,
	OnDestroy,
	output,
	OutputEmitterRef,
	signal,
	Signal,
	viewChild,
	WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, firstValueFrom, Subject, switchMap, tap } from 'rxjs';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { PhButtonComponent } from '../ph-button/ph-button.component';
import {
	ComplianceFeedbackRating, IComplianceResultTag,
	IPhotoReportComplianceMetric,
	IPhotoReportComplianceResult,
	PhotoReportComplianceScore
} from '../../../control/models/photoReport';
import { LightenColorPipe } from '../../pipes/lighten-color.pipe';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { MatLabel } from '@angular/material/input';
import { PhChatInputComponent } from '../ph-chat-input/ph-chat-input.component';
import { IMentionableUsers, INewChatMessage } from '../../models/chat';
import { ChatService } from '../../services/chat.service';
import { ImageRecognitionService } from '../../services/image-recognition.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { MatTooltip } from '@angular/material/tooltip';
import { MentionParserPipe } from '../../pipes/mention-parser.pipe';

interface IFeedbackPayload {
	rating: ComplianceFeedbackRating;
	comment: string;
	showThanks: boolean;
}

type ComplianceMetricKey =
	| 'photo_quality'
	| 'empty_gaps'
	| 'missing_products'
	| 'extra_products'
	| 'product_positions'
	| 'facings';

const METRIC_KEYS: Array<ComplianceMetricKey> = [
	'photo_quality',
	'empty_gaps',
	'missing_products',
	'extra_products',
	'product_positions',
	'facings'
];

const SCORE_COLORS: Map<PhotoReportComplianceScore, string> = new Map([
	[1, '#FF0000'],
	[2, '#FF0000'],
	[3, '#FF5A00'],
	[4, '#E5B60E'],
	[5, '#1AA932']
]);

const PERFECT_SCORE: number = 5;
const ANCHOR_GAP_PX: number = 12;
const VIEWPORT_PADDING_PX: number = 12;

@Component({
	selector: 'app-ir-compliance-result',
	templateUrl: './ir-compliance-result.component.html',
	styleUrl: './ir-compliance-result.component.scss',
	imports: [
		DragDropModule, FormsModule, TranslateModule, PhIconWrapperComponent, PhButtonComponent, LightenColorPipe,
		PhIconComponent, MatLabel, PhChatInputComponent, MatTooltip, MentionParserPipe
	]
})
export class IRComplianceResultComponent implements AfterViewInit, OnDestroy {

	readonly complianceResult: InputSignal<IPhotoReportComplianceResult> = input.required();
	readonly planogramId: InputSignal<number> = input.required();
	readonly photoreportId: InputSignal<number> = input.required();
	readonly executionBlockShown: InputSignal<boolean> = input<boolean>(false);
	readonly anchor: InputSignal<HTMLElement> = input<HTMLElement>();

	onClose: OutputEmitterRef<void> = output<void>();
	onReturn: OutputEmitterRef<INewChatMessage> = output<INewChatMessage>();
	onConfirm: OutputEmitterRef<INewChatMessage> = output<INewChatMessage>();
	onGoToChat: OutputEmitterRef<number> = output<number>();

	private readonly feedbackRequest$ = new Subject<IFeedbackPayload>();
	private resizeObserver: ResizeObserver | null = null;
	readonly scoreMax: number = PERFECT_SCORE;
	readonly scoreColors: Map<PhotoReportComplianceScore, string> = SCORE_COLORS;
	readonly feedback: WritableSignal<ComplianceFeedbackRating> = signal(null);
	feedbackComment: WritableSignal<string> = signal('');
	readonly detailsOpened: WritableSignal<boolean> = signal(false);
	readonly feedbackOpened: WritableSignal<boolean> = signal(false);
	readonly messageOpened: WritableSignal<boolean> = signal(true);
	readonly summaryScore: Signal<number> = computed(() => this.complianceResult().compliance_score);
	readonly summaryColor: Signal<string> = computed(() => SCORE_COLORS.get(Math.floor(this.complianceResult().compliance_score) as PhotoReportComplianceScore));
	readonly summaryComment: Signal<string> = computed(() => this.complianceResult().compliance_comment);
	readonly isUnsuitablePhoto: Signal<boolean> = computed(() => Math.floor(this.complianceResult().photo_quality.score) === 1);
	readonly summaryTags: Signal<Array<IComplianceResultTag>> = computed(() => {
		if (this.isUnsuitablePhoto()) {
			return [{
				color: SCORE_COLORS.get(1),
				text: this.translate.instant('IMAGE_RECOGNITION.PHOTO_UNSUITABLE_FOR_RECOGNITION')
			}];
		}
		return this.complianceResult().tags;
	});
	readonly previousComment: Signal<string> = computed(() => this.complianceResult().comment?.trim() || '');
	readonly previousCommentMessageId: Signal<number | null> = computed(() => this.complianceResult().message_id ?? null);
	readonly feedbackCommentShown: Signal<boolean> = computed(() => this.feedback() === 'DISLIKE');
	readonly initialMessage: Signal<string> = computed(() => {
		const manager: IMentionableUsers = this.chatService.mentionableUsers()
			.find((user: IMentionableUsers) => user.id === this.complianceResult().manager_id);
		const mention: string = manager?.full_name?.trim() ? `@${manager.full_name.trim()}` : '';
		const comment: string = this.summaryComment().trim();
		return [mention, comment].filter(Boolean).join(' ');
	});
	readonly detailMetrics: Signal<Array<{ key: ComplianceMetricKey; metric: IPhotoReportComplianceMetric, color: string }>> = computed(() => {
		const result: IPhotoReportComplianceResult = this.complianceResult();
		const keys: Array<ComplianceMetricKey> = this.isUnsuitablePhoto()
			? ['photo_quality']
			: METRIC_KEYS.filter((key: ComplianceMetricKey) => key !== 'photo_quality');

		return keys.map((key: ComplianceMetricKey) => ({
			key,
			color: SCORE_COLORS.get(Math.floor(result[key].score) as PhotoReportComplianceScore),
			metric: result[key]
		}));
	});

	private readonly panelRef = viewChild.required<ElementRef<HTMLElement>>('panel');
	private readonly chatInput = viewChild(PhChatInputComponent);
	private readonly pointerListenersAbort = new AbortController();
	private pointerPressed: boolean = false;
	private pointerInsideViewport: boolean = true;

	private readonly chatService: ChatService = inject(ChatService);
	private readonly imageRecognitionService: ImageRecognitionService = inject(ImageRecognitionService);
	private readonly snackBarService: SnackBarService = inject(SnackBarService);
	private readonly translate: TranslateService = inject(TranslateService);

	constructor() {
		effect(() => {
			this.feedback.set(this.complianceResult().feedback);
		});

		this.feedbackRequest$.pipe(
			switchMap(({ rating, comment, showThanks }) => this.imageRecognitionService.sendComplianceFeedback(this.planogramId(), {
				photoreport_id: this.photoreportId(),
				rating,
				comment
			}).pipe(
				tap(() => {
					this.complianceResult().feedback = rating;
					if (showThanks) {
						this.snackBarService.setSnackBarNotification({ messageText: 'IMAGE_RECOGNITION.FEEDBACK_THANKS' });
					}
				}),
				catchError(() => {
					this.feedback.set(this.complianceResult().feedback);
					this.snackBarService.setErrorNotification();
					return EMPTY;
				})
			)),
			takeUntilDestroyed()
		).subscribe();
	}

	ngAfterViewInit(): void {
		const panel: HTMLElement = this.panelRef().nativeElement;
		const anchor: HTMLElement = this.anchor();

		if (anchor) {
			const { top, left } = anchor.getBoundingClientRect();
			panel.style.top = `${top}px`;
			panel.style.left = `${Math.max(0, left - panel.offsetWidth - ANCHOR_GAP_PX)}px`;
			panel.style.right = 'auto';
		}

		this.fitPanelInViewport();
		this.bindPointerTracking();
		this.resizeObserver = new ResizeObserver(() => {
			if (this.pointerPressed || !this.pointerInsideViewport) return;
			this.fitPanelInViewport();
		});
		this.resizeObserver.observe(panel);
	}

	ngOnDestroy(): void {
		this.pointerListenersAbort.abort();
		this.resizeObserver?.disconnect();
		this.resizeObserver = null;
	}

	close(): void {
		this.onClose.emit();
	}

	private bindPointerTracking(): void {
		const signal: AbortSignal = this.pointerListenersAbort.signal;
		const onPointerEvent = (event: PointerEvent): void => {
			this.pointerPressed = event.buttons !== 0;
			this.pointerInsideViewport =
				event.clientX >= 0
				&& event.clientY >= 0
				&& event.clientX <= window.innerWidth
				&& event.clientY <= window.innerHeight;
		};

		window.addEventListener('pointerdown', onPointerEvent, { signal });
		window.addEventListener('pointermove', onPointerEvent, { signal });
		window.addEventListener('pointerup', onPointerEvent, { signal });
		window.addEventListener('pointercancel', onPointerEvent, { signal });
		document.documentElement.addEventListener('mouseleave', () => {
			this.pointerInsideViewport = false;
		}, { signal });
	}

	private fitPanelInViewport(): void {
		const panel: HTMLElement = this.panelRef().nativeElement;
		const rect: DOMRect = panel.getBoundingClientRect();
		const overflowBottom: number = rect.bottom - (window.innerHeight - VIEWPORT_PADDING_PX);

		if (overflowBottom <= 0) return;

		panel.style.top = `${Math.max(VIEWPORT_PADDING_PX, rect.top - overflowBottom)}px`;
	}

	toggleDetails(): void {
		this.detailsOpened.update((opened: boolean) => !opened);
	}

	toggleFeedback(): void {
		this.feedbackOpened.update((opened: boolean) => !opened);
	}

	toggleMessages(): void {
		this.messageOpened.update((opened: boolean) => !opened);
	}

	setFeedback(rating: ComplianceFeedbackRating): void {
		if (this.feedback() === rating) return;
		this.feedback.set(rating);
		this.feedbackOpened.set(rating === 'DISLIKE');
		this.feedbackComment.set('');
		this.feedbackRequest$.next({ rating, comment: '', showThanks: false });
	}

	sendFeedbackComment(): void {
		this.feedbackRequest$.next({
			rating: this.feedback(),
			comment: this.feedbackComment().trim(),
			showThanks: true
		});
		this.feedbackComment.set('');
	}

	confirmExecution(): void {
		void this.emitExecutionMessage((message: INewChatMessage) => this.onConfirm.emit(message));
	}

	returnForExecution(): void {
		void this.emitExecutionMessage((message: INewChatMessage) => this.onReturn.emit(message));
	}

	goToChat(): void {
		const messageId: number | null = this.previousCommentMessageId();
		if (messageId) this.onGoToChat.emit(messageId);
	}

	private async emitExecutionMessage(emit: (message: INewChatMessage) => void): Promise<void> {
		const chatInput: PhChatInputComponent | undefined = this.chatInput();
		if (!chatInput) {
			this.onClose.emit();
			emit({ content: '' });
			return;
		}

		if (chatInput.isUploadingAttachments()) return;

		if (chatInput.isSendDisabled()) {
			this.onClose.emit();
			emit({ content: '' });
			return;
		}

		const messagePromise: Promise<INewChatMessage> = firstValueFrom(this.chatService.newMessage$);
		await chatInput.sendMessage();
		const message: INewChatMessage = await messagePromise;

		this.onClose.emit();
		emit(message);
	}
}
